import type { SigningField } from '../types/fields';
import { clampFieldToPage } from './coordinates';
import {
  getPageSizeForField,
  isValidSnapshotField,
  type PageSize,
  type PageSizeWithRotation,
} from './signingFieldState';

export interface RestoreSnapshotResult {
  fields: SigningField[];
  /** Valid snapshot fields that could not be placed on the target document. */
  dropped: number;
}

/**
 * Strip heavy binary payload before persisting snapshots/templates.
 * Geometry, type, value, and style props are preserved.
 */
export function stripFieldsForSnapshot(fields: SigningField[]): SigningField[] {
  return fields.map((f) => ({ ...f, imageDataUrl: undefined }));
}

/**
 * Scale a field's position (and ink points) proportionally when the target page's
 * visual dimensions differ from the snapshot's source page. Box width/height are
 * kept fixed — same contract as duplicateFieldToAllPages.
 */
export function remapFieldToTargetPage(
  field: SigningField,
  sourceVisual: PageSize,
  targetVisual: PageSize
): SigningField {
  const scaleX = sourceVisual.width > 0 ? targetVisual.width / sourceVisual.width : 1;
  const scaleY = sourceVisual.height > 0 ? targetVisual.height / sourceVisual.height : 1;

  const remapped: SigningField = {
    ...field,
    x: field.x * scaleX,
    y: field.y * scaleY,
  };

  if (field.points?.length) {
    remapped.points = field.points.map((p) => ({
      x: p.x * scaleX,
      y: p.y * scaleY,
    }));
  }

  return clampFieldToPage(remapped, targetVisual.width, targetVisual.height);
}

/**
 * Restore snapshot/template fields onto a target document.
 *
 * - Invalid schema entries are dropped (isValidSnapshotField).
 * - Out-of-range pageIndex values are skipped.
 * - When sourcePageSizes are provided, positions are remapped proportionally per page
 *   when visual dimensions differ (e.g. test-contract page 1 → test-mixed-sizes landscape).
 * - Results are clamped to each target page's visual bounds (rotation-aware).
 */
export function restoreSnapshotFields(
  rawFields: unknown[],
  targetPageSizes: ReadonlyArray<PageSizeWithRotation>,
  // Source page sizes are index-aligned with the snapshot's source document.
  // Invalid entries are kept as null placeholders (see templateBundle) so
  // field.pageIndex stays aligned; a null entry means "source size unknown for
  // this page" and the field is skipped rather than misremapped.
  sourcePageSizes?: ReadonlyArray<PageSizeWithRotation | null>
): RestoreSnapshotResult {
  const cloned = structuredClone(rawFields);
  const raw = cloned.filter(isValidSnapshotField) as SigningField[];
  const invalidCount = cloned.length - raw.length;
  const pageCount = targetPageSizes.length;
  const restored: SigningField[] = [];

  for (const field of raw) {
    if (field.pageIndex < 0 || field.pageIndex >= pageCount) {
      console.warn(
        `restoreSnapshotFields: skipping field on page ${field.pageIndex + 1} — ` +
          `target document only has ${pageCount} page(s).`
      );
      continue;
    }

    const targetVisual = getPageSizeForField(field, targetPageSizes);
    if (!targetVisual) {
      console.warn(
        `restoreSnapshotFields: skipping field on page ${field.pageIndex + 1} — ` +
          `target page size data is missing or invalid.`
      );
      continue;
    }

    let toPlace = field;

    if (sourcePageSizes?.length) {
      // Index directly (the array is index-aligned with the source document and
      // may contain null placeholders); resolve the visual size via a synthetic
      // single-entry lookup so all the validity/rotation logic stays centralized.
      const sourceEntry = sourcePageSizes[field.pageIndex];
      const sourceVisual = sourceEntry
        ? getPageSizeForField({ pageIndex: 0 }, [sourceEntry])
        : null;
      if (!sourceVisual) {
        console.warn(
          `restoreSnapshotFields: skipping field on page ${field.pageIndex + 1} — ` +
            `source page size data is missing or invalid for this page.`
        );
        continue;
      }

      if (
        sourceVisual.width !== targetVisual.width ||
        sourceVisual.height !== targetVisual.height
      ) {
        toPlace = remapFieldToTargetPage(field, sourceVisual, targetVisual);
      } else {
        toPlace = clampFieldToPage(field, targetVisual.width, targetVisual.height);
      }
    } else {
      toPlace = clampFieldToPage(field, targetVisual.width, targetVisual.height);
    }

    restored.push(toPlace);
  }

  return { fields: restored, dropped: invalidCount + (raw.length - restored.length) };
}